import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../state/store";
import { fetchCart, updateCartItem, removeFromCart, clearCart } from "../state/slices/cartSlice";
import { useAuth } from "../context/AuthContext";
import MainNavbar from "../pages/MainNavbar";
import Footer from "./footers";
import Loading from "./Loading";
import NoData from "./NoData";
import './CartPage.css';
import Breadcrumbs from "./Breadcrumbs";
import { calculatePrice } from "../utils/pricing"; // ✅ added

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  const [showSubnav, setShowSubnav] = useState(false);
  const [notification, setNotification] = useState<{ 
    message: string; 
    type: 'success' | 'error' 
  } | null>(null);

  const cart = useSelector((state: RootState) => state.cart.cart);
  const cartStatus = useSelector((state: RootState) => state.cart.status);
  const cartError = useSelector((state: RootState) => state.cart.error);
  const products = useSelector((state: RootState) => state.product.items);
  
  const userId = currentUser?.id || "";
  const isLoggedIn = !!currentUser;

  useEffect(() => {
    if (isLoggedIn && userId) {
      dispatch(fetchCart(userId));
    }
  }, [dispatch, isLoggedIn, userId]);

  const handleBuyNow = () => {
    if (!cart || cart.items.length === 0) {
      setNotification({ message: "Your cart is empty", type: "error" });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    navigate("/user/checkout/" + userId);
  };

  const handleUpdateQuantity = async (productId: string, quantity: number) => {
    if (!isLoggedIn) {
      setNotification({ message: "Please login to update cart", type: "error" });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    
    try {
      await dispatch(updateCartItem({ userId, productId, quantity })).unwrap();
    } catch {
      setNotification({ 
        message: cartError || "Failed to update cart", 
        type: "error" 
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleRemoveItem = async (productId: string) => {
    if (!isLoggedIn) {
      setNotification({ message: "Please login to update cart", type: "error" });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    
    try {
      await dispatch(removeFromCart({ userId, productId })).unwrap();
    } catch {
      setNotification({ 
        message: cartError || "Failed to remove item", 
        type: "error" 
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleClearCart = async () => {
    if (!isLoggedIn) return;
    
    try {
      await dispatch(clearCart(userId)).unwrap();
      setNotification({ 
        message: "Cart cleared successfully", 
        type: "success" 
      });
      setTimeout(() => setNotification(null), 3000);
    } catch {
      setNotification({ 
        message: cartError || "Failed to clear cart", 
        type: "error" 
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  // ✅ FIXED pricing logic ONLY
  const cartItems = (cart?.items || []).map(item => {
    const product = products.find(p => p.id === item.productId);
    if (!product) return null;

    const { finalPrice } = calculatePrice(product);

    return {
      ...item,
      ...product,
      price: finalPrice, // override price safely
      subtotal: finalPrice * item.quantity
    };
  }).filter((item): item is NonNullable<typeof item> => item !== null);

  // ✅ FIXED total
  const total = cartItems.reduce((sum, item) => sum + item.subtotal, 0);

  if (cartStatus === "loading") {
    return <Loading />;
  }

  return (
    <div className="cart-page-wrapper">
      <MainNavbar
        searchQuery=""
        setSearchQuery={() => {}}
        isLoggedIn={isLoggedIn}
        showSubnav={showSubnav}
        setShowSubnav={setShowSubnav}
        userId={userId}
        disableSearch={true}
      />

      <Breadcrumbs />

      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="cart-container">
        <div className="cart-header">
          <h2>Your Shopping Cart</h2>
          <p className="cart-subtitle">
            {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in your cart
          </p>
        </div>

        <div className="cart-content">
          {cartItems.length > 0 ? (
            <>
              <div className="cart-items-list">
                {cartItems.map((item) => (
                  <div key={item.productId} className="cart-item">
                    <div className="item-image-container">
                      <img 
                        src={item.images[0]} 
                        alt={item.name} 
                        className="cart-item-image" 
                      />
                    </div>
                    
                    <div className="cart-item-details">
                      <h3 className="item-title">{item.name}</h3>
                      <div className="cart-item-price">₹{item.price.toFixed(2)}</div>
                      
                      <div className="cart-item-controls">
                        <div className="quantity-controls">
                          <button 
                            className="quantity-btn"
                            onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            −
                          </button>
                          <span className="quantity-value">{item.quantity}</span>
                          <button 
                            className="quantity-btn"
                            onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}
                          >
                            +
                          </button>
                        </div>
                        
                        <button 
                          className="cart-item-remove"
                          onClick={() => handleRemoveItem(item.productId)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                    
                    <div className="cart-item-subtotal">
                      <div className="subtotal-label">Subtotal</div>
                      <div className="subtotal-value">₹{item.subtotal.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="cart-summary-container">
                <div className="cart-summary">
                  <div className="summary-header">
                    <h3>Order Summary</h3>
                  </div>
                  
                  <div className="summary-content">
                    <div className="summary-row">
                      <span>Subtotal</span>
                      <span>₹{total.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Shipping</span>
                      <span>Free</span>
                    </div>
                    {/* <div className="summary-row">
                      <span>Tax</span>
                      <span>₹0.00</span> 
                    </div> */}
                    <div className="summary-row total">
                      <span>Total</span>
                      <span>₹{total.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="cart-actions">
                    <button className="clear-cart-btn" onClick={handleClearCart}>
                      Clear Cart
                    </button>
                    <button className="checkout-btn" onClick={handleBuyNow}>
                      Proceed to Checkout
                    </button>
                  </div>
                  
                  <div className="continue-shopping-container">
                    <button className="continue-shopping" onClick={() => navigate("/")}>
                      Continue Shopping
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-cart">
              <div className="empty-cart-icon">
                {/* SVG unchanged */}
              </div>
              <NoData message="Your cart is empty" />
              <p>Looks like you haven't added anything to your cart yet</p>
              <button className="continue-shopping" onClick={() => navigate("/")}>
                Start Shopping
              </button>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CartPage;